import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { GradeSharePreviewImage } from './components/GradeSharePreviewImage';
import { getGradeShare } from './lib/data';
import type { GradeShareData } from './lib/types';
import './share.css';
import styles from './components/preview.module.css';

type LoadState =
  | { status: 'loading' }
  | { status: 'loaded'; data: GradeShareData }
  | { status: 'not-found' }
  | { status: 'error' };

export default function SharePreviewRenderPage() {
  const code = useMemo(() => {
    const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
    const [, rawCode = ''] = path.split('/');
    return decodeURIComponent(rawCode).trim();
  }, []);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [paletteReady, setPaletteReady] = useState(false);
  const onPaletteReady = useCallback(() => setPaletteReady(true), []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await getGradeShare(code);
        if (cancelled) return;
        if (!data) {
          setState({ status: 'not-found' });
          return;
        }
        document.title = `${data.card.name} grade preview | Evo`;
        setState({ status: 'loaded', data });
      } catch {
        if (!cancelled) setState({ status: 'error' });
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [code]);

  return (
    <div className={`share-root ${styles.previewRoot}`}>
      {state.status === 'loaded' ? (
        <PreviewReadyBoundary paletteReady={paletteReady}>
          <GradeSharePreviewImage data={state.data} onPaletteReady={onPaletteReady} />
        </PreviewReadyBoundary>
      ) : (
        <div className={styles.previewState}>
          {state.status === 'loading'
            ? 'Loading preview'
            : state.status === 'error'
              ? 'Unable to load preview'
              : 'Preview not found'}
        </div>
      )}
    </div>
  );
}

function PreviewReadyBoundary({ children, paletteReady }: { children: ReactNode; paletteReady: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!paletteReady) return;
    let cancelled = false;

    async function waitForRender() {
      const root = ref.current;
      if (!root) return;

      const images = Array.from(root.querySelectorAll('img'));
      await Promise.all(
        images.map(
          (img) =>
            img.complete ||
            new Promise<void>((resolve) => {
              img.addEventListener('load', () => resolve(), { once: true });
              img.addEventListener('error', () => resolve(), { once: true });
            }),
        ),
      );

      await document.fonts?.ready;
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (!cancelled) setReady(true);
    }

    waitForRender();
    return () => {
      cancelled = true;
    };
  }, [paletteReady]);

  return (
    <div ref={ref} data-preview-ready={ready ? 'true' : undefined}>
      {children}
    </div>
  );
}
